import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';

// ── Comment pings (Calem, report 1b3f56a6) ──────────────────────────────────────────────────────
// "When a user adds a manual (only) comment and they also include the person's name or nickname …
// send that person a pop-up on their user profile on the screen, asking them to respond. They can
// simply delete my comment … and replace it with his comment." A manual Active Jobs comment that names
// a colleague leaves them a ping on the server (services/jobCommentPing.ts); this watcher pops it up.
// Reply opens Active Jobs on that job with the comment in its edit box, to be replaced with the answer;
// Later hides it for 10 minutes and then it is back, as JobStatusConfirm's Re-check does. It is gone
// for good once they change the job's comment, the comment stops naming them, or the job is closed or
// deleted. Mounted once, app-wide, beside JobStatusConfirm and polled the same way.
// A reply pops up here too — the colleague answered one of the reader's own comments ("make sure the
// person who sent the comment … is notified"), the answer quoted: Open goes to the job the way Reply
// does, OK closes it; either way it is gone for good.

const POLL_MS = 60_000;
// The same pop-up the server keeps: one request per job, one reply per job from each colleague.
const entryKey = (p: any): string => (p.kind === 'reply' ? `reply:${p.jobId}:${p.byUserId}` : `ask:${p.jobId}`);
// Don't steal the keyboard from someone mid-sentence — keyed on keystrokes, never on focus (see the
// regression noted in JobStatusConfirm).
const TYPING_IDLE_MS = 4000;
// Owner 2026-08-25 (same rule as the Jobs lists): a quantity of one ("【1】") is noise in a job title.
const stripQtyOne = (s: any): string => String(s || '').replace(/\s*【\s*1\s*】/g, ' ').replace(/\s{2,}/g, ' ').trim();

export default function JobCommentPing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loc = useLocation();
  const me = useStore(s => s.me);
  // Never on the public/no-auth pages: a client on /w/<token> must not see the office's job talk.
  const publicPage = /^\/(login|w|dd|contract|portal)(\/|$)/.test(loc.pathname);
  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, []);
  const [queue, setQueue] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const meRef = useRef(me);
  meRef.current = me;

  const poll = useCallback(async () => {
    const m = meRef.current;
    // Only people who hold Active Jobs are ever pinged — it is where they answer.
    if (!m || !localStorage.getItem('mf_token') || !(m.modules || []).includes('active_jobs')) return;
    try {
      const { rows } = await api.jobCommentPings();
      setQueue(rows || []);
    } catch { /* transient — try again next tick */ }
  }, []);

  useEffect(() => {
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);
  // Drop everything the moment the user logs out; look again when they sign in or move around.
  useEffect(() => { if (!me) setQueue([]); }, [me]);
  useEffect(() => { poll(); }, [loc.pathname, me?.userId, poll]);

  const current = queue[0];
  if (!me || !current || publicPage || typing) return null;

  const drop = (p: any) => setQueue(q => q.filter(x => entryKey(x) !== entryKey(p)));
  const job = [`#${Number(current.jobNo).toLocaleString()}`, stripQtyOne(current.specific), String(current.client || '').trim()]
    .filter(Boolean).join(' · ');
  const reply = current.kind === 'reply';
  const text = String(current.text || '').trim();
  const openJob = () => navigate(`/m/active_jobs?reply=${encodeURIComponent(current.jobId)}`, { state: { replyText: String(current.text || '') } });

  // Reply: off the screen for the same 10 minutes while they answer (Re-check does the same for a
  // status confirmation), then Active Jobs opens on the job with its comment ready to be replaced.
  const onReply = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.jobCommentPingLater(current.jobId); } catch { /* still opens the job */ }
    drop(current);
    setBusy(false);
    openJob();
  };
  const onLater = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.jobCommentPingLater(current.jobId); } catch { /* the next poll shows it again */ }
    drop(current);
    setBusy(false);
  };
  // A reply: OK closes it for good; Open does too, then opens the job as Reply does.
  const onDone = async (open: boolean) => {
    if (busy) return;
    setBusy(true);
    try { await api.jobCommentPingOk(current.jobId, String(current.byUserId || '')); } catch { /* the next poll shows it again */ }
    drop(current);
    setBusy(false);
    if (open) openJob();
  };

  const btn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' };
  return (
    // One step under JobStatusConfirm (mounted after this, same layer): a status email waiting on its
    // confirmation is the more urgent of the two, so it stays on top.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 480, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '18px 22px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            <i className={reply ? 'ti ti-arrow-back-up' : 'ti ti-at'} style={{ fontSize: 22, color: 'var(--accent)', marginTop: 1 }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.4 }}>
              {t(reply ? 'jcp_replied' : 'jcp_asked', { name: current.byName || '—', job })}
            </div>
            {queue.length > 1 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>1 / {queue.length}</span>}
          </div>
        </div>
        <div style={{ padding: '14px 22px 4px' }}>
          {/* A reply with no words: the colleague only deleted — said so, rather than an empty quote. */}
          <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--c-gray-50)', border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.5, color: text || !reply ? 'var(--text-primary)' : 'var(--text-tertiary)', fontStyle: text || !reply ? undefined : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {text || !reply ? `“${text}”` : t('jcp_replied_none')}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 8, lineHeight: 1.5 }}>{t(reply ? 'jcp_replied_hint' : 'jcp_hint')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 22px 20px' }}>
          {reply ? (
            <>
              <button onClick={() => onDone(true)} disabled={busy} style={{ ...btn, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
                <i className="ti ti-eye" style={{ fontSize: 14 }} aria-hidden />{t('jcp_open')}
              </button>
              <button onClick={() => onDone(false)} disabled={busy} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
                <i className="ti ti-check" style={{ fontSize: 14 }} aria-hidden />{t('jcp_ok')}
              </button>
            </>
          ) : (
            <>
              <button onClick={onReply} disabled={busy} style={{ ...btn, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
                <i className="ti ti-message-circle" style={{ fontSize: 14 }} aria-hidden />{t('jcp_reply')}
              </button>
              <button onClick={onLater} disabled={busy} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
                <i className="ti ti-clock" style={{ fontSize: 14 }} aria-hidden />{t('jcp_later')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
